import React, { useState, useEffect, useCallback } from 'react';
import InterviewCard from './InterviewCard';
import InterviewDetailModal from './InterviewDetailModal';

// Ensure IntersectionObserver is available in the browser environment
const hasIntersectionObserver = typeof window !== 'undefined' && 'IntersectionObserver' in window;

/**
 * Component that lazy loads interviews as user scrolls
 * for better performance with large lists
 * 
 * Features:
 * - Lazy loading with infinite scroll
 * - Clickable cards that open detailed modal
 * - Status updates through modal interface
 * - Real-time data synchronization
 * - Scroll-to-top functionality
 */
const LazyInterviewList = ({
  interviews,
  viewMode,
  onReschedule,
  onCancel,
  onComplete,
  onMessage,
  onStatusChange
}) => {
  // Configure how many items to load initially and in each batch
  const INITIAL_LOAD = 8;
  const BATCH_SIZE = 8;
  
  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const visibleInterviews = interviews.slice(0, visibleCount);
  
  // Check if we need to load more interviews
  const hasMore = visibleCount < interviews.length;
  
  // Handle card click to open modal
  const handleCardClick = (interview) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[LazyInterviewList] Card clicked, opening modal for interview:', {
        id: interview.id,
        status: interview.status,
        candidate: interview.candidate_name || interview.applicant?.full_name,
        job: interview.job_title || interview.job?.title
      });
    }
    
    setSelectedInterview(interview);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[LazyInterviewList] Modal closed');
    }
    
    setIsModalOpen(false);
    setSelectedInterview(null);
  };

  // Handle status change from modal
  const handleModalStatusChange = async (interviewId, newStatus, interviewData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[LazyInterviewList] Handling status change from modal:', {
        interviewId,
        newStatus
      });
    }
    
    try {
      // Forward optional interviewData (used when rescheduling to update local state)
      await onStatusChange(interviewId, newStatus, interviewData);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[LazyInterviewList] Status change successful, updating local state');
      }
      
      // Update the selected interview if it's the same one
      if (selectedInterview && selectedInterview.id === interviewId) {
        // If reschedule provided a full interview object, merge it in
        const updatedInterview = interviewData && interviewData.id === selectedInterview.id
          ? { ...selectedInterview, ...interviewData }
          : { ...selectedInterview, status: newStatus };
        setSelectedInterview(updatedInterview);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[LazyInterviewList] Updated selected interview:', updatedInterview);
        }
      }
    } catch (error) {
      console.error('[LazyInterviewList] Error updating interview status:', {
        interviewId,
        newStatus,
        error: error.message || error
      });
      // Keep modal open on error so user can retry
      throw error; // Re-throw to let the modal handle it
    }
  };
  
  // Function to handle intersection observer callback
  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting && hasMore) {
      // Load more items when the sentinel element is visible
      setVisibleCount(prev => Math.min(prev + BATCH_SIZE, interviews.length));
    }
  }, [hasMore, interviews.length]);
  
  // Set up intersection observer when component mounts or dependencies change
  useEffect(() => {
    // Check if IntersectionObserver is available
    if (!hasIntersectionObserver) {
      // Fallback for browsers without IntersectionObserver support
      if (hasMore) {
        setVisibleCount(prev => Math.min(prev + BATCH_SIZE, interviews.length));
      }
      return;
    }
    
    const option = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: 0.1
    };
    
    const observer = new window.IntersectionObserver(handleObserver, option);
    const sentinel = document.querySelector('#interview-load-more-sentinel');
    
    if (sentinel) observer.observe(sentinel);
    
    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [handleObserver, visibleCount, hasMore, interviews.length]);
  
  // When the interviews array changes, reset visible count to initial load
  useEffect(() => {
    setVisibleCount(INITIAL_LOAD);
  }, [interviews]);

  // Update selected interview when interviews change (for real-time sync)
  useEffect(() => {
    if (selectedInterview) {
      const updatedInterview = interviews.find(int => int.id === selectedInterview.id);
      if (updatedInterview && JSON.stringify(updatedInterview) !== JSON.stringify(selectedInterview)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[LazyInterviewList] Syncing selected interview with updated data:', {
            interviewId: selectedInterview.id,
            oldStatus: selectedInterview.status,
            newStatus: updatedInterview.status,
            changes: Object.keys(updatedInterview).filter(key => 
              JSON.stringify(updatedInterview[key]) !== JSON.stringify(selectedInterview[key])
            )
          });
        }
        setSelectedInterview(updatedInterview);
      }
    }
  }, [interviews, selectedInterview]);
  
  // Scroll to top functionality
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Check scroll position to determine if we should show the scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Smooth scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <>
      <div 
        className={viewMode === 'grid' 
          ? "grid grid-cols-1 md:grid-cols-2 gap-4" 
          : "space-y-4"
        }
      >
        {visibleInterviews.map((interview) => (
          <InterviewCard
            key={interview.id}
            data-interview-id={interview.id}
            interview={interview}
            view={viewMode}
            onReschedule={() => onReschedule?.(interview.id)}
            onCancel={() => onCancel?.(interview.id)}
            onComplete={() => onComplete?.(interview.id)}
            onMessage={onMessage ? () => onMessage(interview) : undefined}
            onStatusChange={onStatusChange}
            isClickable={true}
            onClick={handleCardClick}
          />
        ))}

      </div>
      
      {/* Sentinel element for infinite scroll */}
      {hasMore && (
        <div 
          id="interview-load-more-sentinel" 
          className="h-10 flex items-center justify-center my-4"
        >
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Interview Detail Modal */}
      <InterviewDetailModal
        interview={selectedInterview}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onStatusChange={handleModalStatusChange}
      />
    </>
  );
};

// Export both default and named export to ensure it's properly recognized
export { LazyInterviewList };
export default LazyInterviewList;
